import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useT, type T } from "@/i18n";
import { nameIn } from "@/services/articles";
import { fetchLabels, saveLabelPref } from "@/services/labels";
import {
  CARTON_FORMATS, LABEL_KITS, cartonLabelsDocument, cleanSupplierRef, knownCode, labelInfo, openPrintTab, supplierRefFor,
  type CartonFormat, type CartonLine,
} from "@/lib/cartonLabels";

// Z Production's "🏷️ Étiquettes cartons" (one reception) and "🗂️ Base d'étiquettes" (every reference),
// his openCartonLabels / openLabelLibrary.

const FORMAT_KEY = "myotaku-warehouse:carton-format";
function useCartonFormat() {
  const [fmt, setFmt] = useState<CartonFormat>(() => {
    try { const v = localStorage.getItem(FORMAT_KEY); if (v && v in CARTON_FORMATS) return v as CartonFormat; } catch { /* storage blocked */ }
    return "a4";
  });
  return [fmt, (f: CartonFormat) => { setFmt(f); try { localStorage.setItem(FORMAT_KEY, f); } catch { /* this tab only */ } }] as const;
}

function FormatChoice({ fmt, onChange, perPage }: { fmt: CartonFormat; onChange: (f: CartonFormat) => void; perPage: boolean }) {
  const { t } = useT();
  const f = CARTON_FORMATS[fmt];
  return (
    <div className="mb-2">
      <label className="flex flex-wrap items-center gap-2 text-sm text-slate-700">{t("carton.printOn")}
        <select value={fmt} onChange={(e) => onChange(e.target.value as CartonFormat)} className="min-w-[220px] flex-1 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
          {(Object.keys(CARTON_FORMATS) as CartonFormat[]).map((k) => <option key={k} value={k}>{t(`carton.fmt.${k}`)}</option>)}
        </select>
      </label>
      <p className="mt-1 text-xs text-slate-500">
        {f.w ? t(perPage ? "carton.helpQlPage" : "carton.helpQl", { w: f.w, h: f.h }) : t(perPage ? "carton.helpA4Page" : "carton.helpA4")}
      </p>
    </div>
  );
}

const unitWord = (t: T, fmt: CartonFormat) => t(CARTON_FORMATS[fmt].w ? "carton.labels" : "carton.sheets");

function Sheets({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const set = (v: number) => onChange(Math.max(0, Math.min(200, Math.floor(Number(v) || 0))));
  return (
    <span className="inline-flex items-center gap-1">
      <button onClick={() => set(value - 1)} className="h-7 w-7 rounded border border-slate-300 text-sm">−</button>
      <input type="number" min={0} max={200} value={value} onChange={(e) => set(Number(e.target.value))} className="w-14 rounded border border-slate-300 px-1 py-1 text-center text-sm" />
      <button onClick={() => set(value + 1)} className="h-7 w-7 rounded border border-slate-300 text-sm">+</button>
    </span>
  );
}

function Shell({ title, sub, onClose, children, foot }: { title: string; sub: string; onClose: () => void; children: React.ReactNode; foot: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-scrim/40 p-4" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-4xl rounded-xl bg-white shadow-lg">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-3">
          <div><h3 className="font-semibold text-slate-900">{title}</h3><p className="text-xs text-slate-500">{sub}</p></div>
          <button onClick={onClose} className="rounded-lg px-2 py-1 text-sm ring-1 ring-slate-300">✕</button>
        </div>
        <div className="px-5 py-3">{children}</div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-5 py-3">{foot}</div>
      </div>
    </div>
  );
}

function print(t: T, locale: Parameters<typeof cartonLabelsDocument>[4], lines: CartonLine[], footer: string, title: string, fmt: CartonFormat) {
  const doc = cartonLabelsDocument(lines.filter((l) => l.sheets > 0), footer, title, fmt, locale);
  if (!doc) return { ok: false, msg: t("carton.nothing") };
  return openPrintTab(doc) ? { ok: true, msg: "" } : { ok: false, msg: t("carton.blocked") };
}

// ---- one reception -----------------------------------------------------------------------------

export type ReceivedItem = { reference: string; qty: number; supplier_ref?: string | null };

export function ReceptionLabels({ items, supplier, date, onClose }: { items: ReceivedItem[]; supplier: string | null; date: string; onClose: () => void }) {
  const { t, locale } = useT();
  const [fmt, setFmt] = useCartonFormat();
  const labels = useQuery({ queryKey: ["labels"], queryFn: fetchLabels });
  const footer = t(supplier ? "carton.footerSupplier" : "carton.footer", { date, supplier });
  const [lines, setLines] = useState<(CartonLine & { supEdited: boolean })[]>(() => {
    const seen = new Set<string>();
    return items.filter((i) => !seen.has(i.reference) && seen.add(i.reference)).map((i) => ({
      info: labelInfo(i.reference), sheets: 1, qty: String(i.qty), supRef: cleanSupplierRef(i.supplier_ref), supEdited: !!i.supplier_ref,
    }));
  });
  // Once the references are known, an empty supplier reference takes the one read for this supplier.
  const shown = useMemo(() => lines.map((l) => (l.supEdited || l.supRef ? l
    : { ...l, supRef: supplierRefFor(labels.data?.supplier_refs, l.info.code, supplier) })), [lines, labels.data, supplier]);
  const [all, setAll] = useState(1);
  const [add, setAdd] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; msg: string } | null>(null);
  const update = (i: number, patch: Partial<CartonLine & { supEdited: boolean }>) => setLines((ls) => ls.map((l, k) => (k === i ? { ...shown[k], ...patch } : l)));
  const total = shown.reduce((a, l) => a + l.sheets, 0);
  const f = CARTON_FORMATS[fmt];
  const addRef = () => {
    const raw = add.trim().toUpperCase().replace(/\s+/g, "");
    if (!raw) return;
    const code = knownCode(raw);
    if (!code) { setMsg({ ok: false, msg: t("carton.unknown", { ref: raw }) }); return; }
    setMsg(null);
    if (!lines.some((l) => l.info.code === code)) setLines((ls) => [...ls, { info: labelInfo(code), sheets: 1, qty: "", supRef: "", supEdited: false }]);
    setAdd("");
  };
  return (
    <Shell title={`🏷️ ${t("carton.title")}`} sub={`${footer} — ${t("carton.sub")}`} onClose={onClose}
      foot={<>
        <span className="text-sm font-medium text-slate-700">{f.w ? t(total === 1 ? "carton.nLabel" : "carton.nLabels", { n: total, w: f.w, h: f.h }) : t(total === 1 ? "carton.nSheet" : "carton.nSheets", { n: total, labels: total * 8 })}</span>
        <span className="flex gap-2">
          <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("cancel")}</button>
          <button disabled={total === 0} onClick={() => setMsg(print(t, locale, shown, footer, t("carton.fileReception", { supplier: supplier ?? "", date }), fmt))}
            className="rounded-lg bg-orange-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">🖨️ {t("carton.print")}</button>
        </span>
      </>}>
      <FormatChoice fmt={fmt} onChange={setFmt} perPage={false} />
      <p className="mb-2 text-xs text-slate-500">{t("carton.editHint")}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500"><th className="py-1">{t("carton.ref")}</th><th>{t("carton.article")}</th><th>{t("carton.supRefCol")}</th><th className="text-center">{t("carton.qtyCol")}</th><th className="text-center">{unitWord(t, fmt)}</th></tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={5} className="py-3 text-slate-400">{t("carton.noneRead")}</td></tr>}
            {shown.map((l, i) => (
              <tr key={l.info.code} className="border-t border-slate-100">
                <td className="py-1.5 font-mono font-semibold">{l.info.code}</td>
                <td>{l.info.item ? nameIn(l.info.item, locale) : l.info.code}</td>
                <td><input value={l.supRef} onChange={(e) => update(i, { supRef: e.target.value, supEdited: true })} placeholder="—" className="w-36 rounded border border-slate-300 px-2 py-1 font-mono text-xs" /></td>
                <td className="text-center"><input value={l.qty} onChange={(e) => update(i, { qty: e.target.value })} inputMode="numeric" placeholder="—" className="w-16 rounded border border-slate-300 px-1 py-1 text-center font-mono font-bold" /></td>
                <td className="text-center"><Sheets value={l.sheets} onChange={(n) => update(i, { sheets: n })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        {t("carton.allRefs")}
        <input type="number" min={0} max={50} value={all} onChange={(e) => setAll(Math.max(0, Math.min(200, Number(e.target.value) || 0)))} className="w-16 rounded border border-slate-300 px-1 py-1 text-center" />
        <button onClick={() => setLines(shown.map((l) => ({ ...l, sheets: all })))} className="rounded-lg px-3 py-1 ring-1 ring-slate-300">{t("carton.apply")}</button>
        <span className="ml-auto">{t("carton.addRef")}</span>
        <input value={add} onChange={(e) => setAdd(e.target.value)} placeholder={t("carton.addPh")} className="w-28 rounded border border-slate-300 px-2 py-1" />
        <button onClick={addRef} className="rounded-lg px-3 py-1 ring-1 ring-slate-300">+ {t("carton.add")}</button>
      </div>
      {msg?.msg && <p className={`mt-2 text-sm ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.msg}</p>}
    </Shell>
  );
}

// ---- every reference ---------------------------------------------------------------------------

const fold = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();

export function LabelLibrary({ onClose }: { onClose: () => void }) {
  const { t, locale } = useT();
  const [fmt, setFmt] = useCartonFormat();
  const labels = useQuery({ queryKey: ["labels"], queryFn: fetchLabels });
  const rows = useMemo(() => {
    const seen = new Set<string>();
    return LABEL_KITS.flatMap((k) => k.items.filter((i) => !seen.has(i.code) && seen.add(i.code)).map((i) => ({ kit: k, info: labelInfo(i.code) })));
  }, []);
  // What was typed here; otherwise what the base remembers; otherwise the supplier's reference.
  const [typed, setTyped] = useState<Record<string, { supRef?: string; qty?: string }>>({});
  const [sheets, setSheets] = useState<Record<string, number>>({});
  const [saved, setSaved] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [kit, setKit] = useState("");
  const [footer, setFooter] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; msg: string } | null>(null);
  const supOf = (code: string) => typed[code]?.supRef ?? labels.data?.prefs[code]?.supplier_ref ?? supplierRefFor(labels.data?.supplier_refs, code, "");
  const qtyOf = (code: string) => typed[code]?.qty ?? labels.data?.prefs[code]?.qty ?? "";
  const lineOf = (code: string, n: number, single = false): CartonLine => ({ info: labelInfo(code), sheets: n, qty: qtyOf(code), supRef: supOf(code), single });
  const remember = (code: string) => {
    saveLabelPref(code, { supplier_ref: supOf(code), qty: qtyOf(code) })
      .then(() => { setSaved(code); setTimeout(() => setSaved((s) => (s === code ? null : s)), 1800); })
      .catch((e: Error) => setMsg({ ok: false, msg: t("carton.saveFailed", { reason: e.message }) }));
  };
  const needle = fold(q);
  const visible = rows.filter((r) => (!kit || r.kit.key === kit) && (!needle
    || fold(`${r.info.code} ${r.info.item ? [r.info.item.name, r.info.item.nameEn, r.info.item.nameDe, r.info.item.nameIt].join(" ") : ""} ${supOf(r.info.code)}`).includes(needle)));
  const selected = rows.filter((r) => (sheets[r.info.code] ?? 0) > 0);
  const total = selected.reduce((a, r) => a + sheets[r.info.code], 0);
  const f = CARTON_FORMATS[fmt];
  let lastKit = "";
  return (
    <Shell title={`🗂️ ${t("carton.libTitle")}`} sub={t("carton.libSub")} onClose={onClose}
      foot={<>
        <span className="text-sm font-medium text-slate-700">{!total ? t("carton.noneSelected")
          : t("carton.libTotal", { refs: selected.length, what: f.w ? t(total === 1 ? "carton.nLabelShort" : "carton.nLabelsShort", { n: total }) : t(total === 1 ? "carton.nSheet" : "carton.nSheets", { n: total, labels: total * 8 }) })}</span>
        <span className="flex flex-wrap gap-2">
          <button onClick={() => setSheets({})} className="rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("carton.reset")}</button>
          <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("cartLabel.close")}</button>
          <button disabled={total === 0} onClick={() => setMsg(print(t, locale, selected.map((r) => lineOf(r.info.code, sheets[r.info.code])), footer.trim(), t("carton.fileLibrary"), fmt))}
            className="rounded-lg bg-orange-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40">🖨️ {t("carton.printSelection")}</button>
        </span>
      </>}>
      <FormatChoice fmt={fmt} onChange={setFmt} perPage />
      <div className="mb-2 flex flex-wrap gap-2">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("carton.searchPh")} className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        <select value={kit} onChange={(e) => setKit(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
          <option value="">{t("carton.allKits")}</option>
          {LABEL_KITS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
      </div>
      <label className="mb-2 flex flex-wrap items-center gap-2 text-sm text-slate-700">{t("carton.footerField")}
        <input value={footer} onChange={(e) => setFooter(e.target.value.slice(0, 60))} maxLength={60} placeholder={t("carton.footerPh")} className="min-w-[200px] flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
      </label>
      <div className="max-h-[55vh] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white"><tr className="text-left text-xs text-slate-500"><th className="py-1">{t("carton.ref")}</th><th>{t("carton.article")}</th><th>{t("carton.supRefCol")}</th><th className="text-center">{t("carton.qtyShort")}</th><th className="text-center">{unitWord(t, fmt)}</th><th /></tr></thead>
          <tbody>
            {visible.length === 0 && <tr><td colSpan={6} className="py-3 text-slate-400">{t("carton.noMatch")}</td></tr>}
            {visible.map((r) => {
              const code = r.info.code, n = sheets[code] ?? 0;
              const head = r.kit.key !== lastKit ? (lastKit = r.kit.key, <tr key={`k-${r.kit.key}`}><td colSpan={6} className="bg-slate-50 px-1 py-1 text-xs font-semibold uppercase text-slate-500">{r.kit.label}</td></tr>) : null;
              return [head, (
                <tr key={code} className={`border-t border-slate-100 ${n > 0 ? "bg-orange-50" : ""}`}>
                  <td className="py-1.5 font-mono font-semibold">{code}</td>
                  <td>{r.info.item ? nameIn(r.info.item, locale) : code}</td>
                  <td className="whitespace-nowrap"><input value={supOf(code)} onChange={(e) => setTyped((s) => ({ ...s, [code]: { ...s[code], supRef: e.target.value } }))} onBlur={() => typed[code] && remember(code)}
                    placeholder="—" className="w-36 rounded border border-slate-300 px-2 py-1 font-mono text-xs" />
                    {saved === code && <span className="ml-1 text-xs text-emerald-700">✓ {t("carton.remembered")}</span>}</td>
                  <td className="text-center"><input value={qtyOf(code)} onChange={(e) => setTyped((s) => ({ ...s, [code]: { ...s[code], qty: e.target.value } }))} onBlur={() => typed[code] && remember(code)}
                    inputMode="numeric" placeholder="—" className="w-16 rounded border border-slate-300 px-1 py-1 text-center font-mono font-bold" /></td>
                  <td className="text-center"><Sheets value={n} onChange={(v) => setSheets((s) => ({ ...s, [code]: v }))} /></td>
                  <td><button title={t("carton.printOne")} onClick={() => setMsg(print(t, locale, [lineOf(code, 1, true)], footer.trim(), t("carton.fileOne", { code }), fmt))} className="rounded px-2 py-1 ring-1 ring-slate-300">🖨️</button></td>
                </tr>
              )];
            })}
          </tbody>
        </table>
      </div>
      {msg?.msg && <p className={`mt-2 text-sm ${msg.ok ? "text-emerald-700" : "text-red-600"}`}>{msg.msg}</p>}
    </Shell>
  );
}
